import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import '../../assets/css/project.css';
import {
  initializeFaroMonitoring,
  trackSessionEvent,
  prepareEventAttributes,
} from './monitoring/faro';
import posthog from 'posthog-js';

/* Project specific Javascript goes here. */

window.DataTru = window.DataTru || {};

// Initialize Grafana Faro monitoring as early as possible
(function initializeFaro() {
  // Get Faro configuration from Django template context
  const faroConfig = window.faroConfig;
  const debugEnabled = Boolean(faroConfig?.debugLogging);
  const debug = (level, ...args) => {
    if (window.DataTru?.debugLog) {
      window.DataTru.debugLog(level, ...args);
    } else if (debugEnabled) {
      const logger = console[level] || console.log;
      logger(...args);
    }
  };

  if (faroConfig && faroConfig.enabled) {
    debug('log', '[Faro] Initializing with config:', faroConfig);

    try {
      const faro = initializeFaroMonitoring(faroConfig);

      // Store Faro instance in a namespace to avoid conflicts
      if (faro) {
        window.DataTru.faro = faro;
        window.DataTru.sessionContext = faroConfig.session || {};
        window.DataTru.debugLogging = debugEnabled;
        window.DataTru.faroConfig = faroConfig;

        // Add convenient helper functions to the DataTru namespace
        window.DataTru.trackEvent = (eventName, attributes = {}) => {
          debug('log', '[Faro] Tracking event:', eventName, attributes);
          if (faro && faro.api) {
            try {
              faro.api.pushEvent(
                eventName,
                prepareEventAttributes(attributes, {
                  defaultLevel: 'info',
                  eventType: 'custom',
                }),
              );
              debug('log', '[Faro] Event sent successfully');
            } catch (error) {
              debug('error', '[Faro] Error sending event:', error);
            }
          }
        };

        window.DataTru.trackError = (error, context = {}) => {
          debug('log', '[Faro] Tracking error:', error, context);
          if (faro && faro.api) {
            try {
              faro.api.pushError(
                error,
                prepareEventAttributes(context, {
                  defaultLevel: 'error',
                  eventType: 'error',
                }),
              );
              debug('log', '[Faro] Error sent successfully');
            } catch (err) {
              debug('error', '[Faro] Error sending error event:', err);
            }
          }
        };

        window.DataTru.trackSessionEvent = (eventName, attributes = {}) => {
          debug('log', '[Faro] Tracking session event:', eventName, attributes);
          trackSessionEvent(eventName, attributes);
        };

        debug(
          'log',
          '[Faro] Successfully initialized and stored in DataTru namespace',
        );

        // Send an initial page load event to test the connection
        window.DataTru.trackEvent('faro_initialized', {
          page: window.location.pathname,
          sessionState: (faroConfig.session || {}).state,
        });

        // Emit a session state event so dashboards can segment auth vs anonymous users
        trackSessionEvent('session_state', {
          state: (faroConfig.session || {}).state,
          isAuthenticated: faroConfig.user?.isAuthenticated || false,
          behaviors: (faroConfig.session || {}).behaviors || [],
          requestCount: (faroConfig.session || {}).requestCount || 0,
          durationSeconds: (faroConfig.session || {}).durationSeconds || null,
        });
      }
    } catch (error) {
      debug('error', '[Faro] Initialization failed:', error);
    }
  } else {
    debug('warn', '[Faro] Not enabled or config missing:', faroConfig);
  }
})();

// PostHog pricing-page analytics (§6.4 of pricing spec)
(function initializePostHog() {
  var cfg = window.posthogConfig;
  if (!cfg || !cfg.enabled || !cfg.api_key) {
    window.DataTru.track = function () {}; // no-op fallback
    return;
  }
  try {
    posthog.init(cfg.api_key, {
      api_host: cfg.host,
      person_profiles: 'identified_only',
      capture_pageview: false, // we emit pricing_viewed explicitly
      capture_pageleave: false,
    });
    window.DataTru.track = function (event, props) {
      try {
        posthog.capture(event, props || {});
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[PostHog] capture failed', err);
        }
      }
    };
  } catch (err) {
    window.DataTru.track = function () {};
  }

  // Delegated listener for declarative data-ph-event attributes.
  // Usage: <button data-ph-event="plan_cta_clicked" data-ph-plan_key="starter_v1">
  // Any data-ph-<name> attribute becomes a property on the captured event.
  document.addEventListener('click', function (evt) {
    var el = evt.target.closest('[data-ph-event]');
    if (!el) {
      return;
    }
    var event = el.getAttribute('data-ph-event');
    if (!event) {
      return;
    }
    var props = {};
    Array.prototype.forEach.call(el.attributes, function (attr) {
      if (
        attr.name.indexOf('data-ph-') === 0 &&
        attr.name !== 'data-ph-event'
      ) {
        props[attr.name.slice('data-ph-'.length)] = attr.value;
      }
    });
    window.DataTru.track(event, props);
  });

  // Page-load events: emit data-ph-page="<name>" on document.body or
  // a single root element per page.
  document.addEventListener('DOMContentLoaded', function () {
    var pageEl = document.querySelector('[data-ph-page]');
    if (pageEl && window.DataTru && window.DataTru.track) {
      window.DataTru.track(pageEl.getAttribute('data-ph-page') + '_viewed', {});
    }
  });

  // `<details>` open -> fire `data-ph-event-on-open`.
  document.addEventListener(
    'toggle',
    function (evt) {
      var el = evt.target;
      if (!el || el.tagName !== 'DETAILS' || !el.open) {
        return;
      }
      var event = el.getAttribute('data-ph-event-on-open');
      if (!event) {
        return;
      }
      var props = {};
      Array.prototype.forEach.call(el.attributes, function (attr) {
        if (
          attr.name.indexOf('data-ph-') === 0 &&
          attr.name !== 'data-ph-event-on-open'
        ) {
          props[attr.name.slice('data-ph-'.length)] = attr.value;
        }
      });
      window.DataTru.track(event, props);
    },
    true,
  );
})();

const parseLocalDate = (value) => {
  if (!value) {
    return null;
  }

  const parts = String(value)
    .split('-')
    .map((part) => Number(part));
  if (parts.length !== 3) {
    return null;
  }

  const [year, month, day] = parts;
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatLocalDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const displayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatDisplayDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return displayFormatter.format(date);
};

const positionPicker = ($datepicker, anchorInput, container) => {
  const rect = anchorInput.getBoundingClientRect();
  const offset = 12;
  const scrollY = window.scrollY || window.pageYOffset;
  const scrollX = window.scrollX || window.pageXOffset;
  const pickerHeight = $datepicker.offsetHeight || 0;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldFlip =
    pickerHeight &&
    spaceBelow < pickerHeight + offset &&
    spaceAbove >= pickerHeight + offset;

  let left = rect.left + scrollX;
  let top = shouldFlip
    ? rect.top + scrollY - pickerHeight - offset
    : rect.bottom + scrollY + offset;

  if (container) {
    const containerRect = container.getBoundingClientRect();
    const containerScrollLeft =
      'scrollLeft' in container ? container.scrollLeft : 0;
    const containerScrollTop =
      'scrollTop' in container ? container.scrollTop : 0;
    left = rect.left - containerRect.left + containerScrollLeft;
    top = shouldFlip
      ? rect.top -
        containerRect.top +
        containerScrollTop -
        pickerHeight -
        offset
      : rect.bottom - containerRect.top + containerScrollTop + offset;
  }

  $datepicker.style.left = `${left}px`;
  $datepicker.style.top = `${top}px`;
};

const getScrollParents = (element) => {
  const parents = [];
  let current = element?.parentElement;
  const scrollPattern = /(auto|scroll|overlay)/;

  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflow = `${style.overflow}${style.overflowX}${style.overflowY}`;
    if (scrollPattern.test(overflow)) {
      parents.push(current);
    }
    current = current.parentElement;
  }

  parents.push(window);
  return parents;
};

const syncPickerFromInputs = (picker, start, end) => {
  const startDate = parseLocalDate(start.value);
  const endDate = parseLocalDate(end.value);
  const nextDates = [];

  if (startDate) {
    nextDates.push(startDate);
  }

  if (endDate) {
    nextDates.push(endDate);
  }

  if (!nextDates.length) {
    picker.clear({ silent: true });
    return;
  }

  picker.selectDate(nextDates, { silent: true });
};

const syncDisplayFromValues = (
  startDisplay,
  endDisplay,
  startValue,
  endValue,
) => {
  const startDate = parseLocalDate(startValue.value);
  const endDate = parseLocalDate(endValue.value);

  startDisplay.value = startDate ? formatDisplayDate(startDate) : '';
  endDisplay.value = endDate ? formatDisplayDate(endDate) : '';
};

const dispatchValueChange = (input) => {
  if (!input) {
    return;
  }
  input.dispatchEvent(new Event('change'));
};

const createHiddenAnchor = (start) => {
  const anchor = document.createElement('input');
  anchor.type = 'text';
  anchor.autocomplete = 'off';
  anchor.tabIndex = -1;
  anchor.setAttribute('aria-hidden', 'true');
  anchor.className = 'air-datepicker-anchor';
  anchor.style.cssText =
    'position:absolute; opacity:0; pointer-events:none; width:0; height:0;';

  const container = start.parentElement;
  if (container && window.getComputedStyle(container).position === 'static') {
    container.style.position = 'relative';
  }

  (container || document.body).appendChild(anchor);
  return anchor;
};

const initializeRangeDatepickers = () => {
  const groupedInputs = new Map();

  document
    .querySelectorAll('[data-air-date-group][data-air-date-role]')
    .forEach((input) => {
      const group = input.dataset.airDateGroup;
      const role = input.dataset.airDateRole;
      if (!group || !role) {
        return;
      }

      if (!groupedInputs.has(group)) {
        groupedInputs.set(group, {});
      }

      groupedInputs.get(group)[`${role}Display`] = input;
    });

  document
    .querySelectorAll('[data-air-date-group][data-air-date-value]')
    .forEach((input) => {
      const group = input.dataset.airDateGroup;
      const role = input.dataset.airDateValue;
      if (!group || !role) {
        return;
      }

      if (!groupedInputs.has(group)) {
        groupedInputs.set(group, {});
      }

      groupedInputs.get(group)[`${role}Value`] = input;
    });

  groupedInputs.forEach(
    ({ startDisplay, endDisplay, startValue, endValue }, group) => {
      if (!startDisplay || !endDisplay || !startValue || !endValue) {
        return;
      }

      const selectedDates = [];
      const startDate = parseLocalDate(startValue.value);
      const endDate = parseLocalDate(endValue.value);
      let lastSelectedCount = 0;

      if (startDate) {
        selectedDates.push(startDate);
      }

      if (endDate) {
        selectedDates.push(endDate);
      }
      lastSelectedCount = selectedDates.length;

      syncDisplayFromValues(startDisplay, endDisplay, startValue, endValue);

      const anchor = createHiddenAnchor(startDisplay);
      const container = startDisplay.closest('[data-air-date-container]');
      const containerGroups = container?.dataset.airDateContainer
        ? container.dataset.airDateContainer.split(/\s+/)
        : [];
      const datepickerContainer = containerGroups.includes(group)
        ? container
        : undefined;
      let isPickerOpen = false;
      let removeGlobalListeners = null;
      let removePositionListeners = null;

      const isEventInside = (event, element) =>
        element && (element === event.target || element.contains(event.target));

      const repositionPicker = () => {
        if (!isPickerOpen || !picker?.$datepicker) {
          return;
        }
        positionPicker(picker.$datepicker, startDisplay, datepickerContainer);
      };

      const handleDocumentMouseDown = (event) => {
        if (!isPickerOpen) {
          return;
        }

        const datepickerEl = picker?.$datepicker;
        const clickedInsideInput =
          isEventInside(event, startDisplay) ||
          isEventInside(event, endDisplay);
        const clickedInsidePicker = isEventInside(event, datepickerEl);

        if (!clickedInsideInput && !clickedInsidePicker) {
          picker.hide();
        }
      };

      const handleDocumentKeyDown = (event) => {
        if (!isPickerOpen) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          picker.hide();
        }
      };

      const addGlobalListeners = () => {
        if (removeGlobalListeners) {
          return;
        }

        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('keydown', handleDocumentKeyDown);
        removeGlobalListeners = () => {
          document.removeEventListener('mousedown', handleDocumentMouseDown);
          document.removeEventListener('keydown', handleDocumentKeyDown);
          removeGlobalListeners = null;
        };
      };

      const addPositionListeners = () => {
        if (removePositionListeners) {
          return;
        }

        const parents = getScrollParents(startDisplay);
        parents.forEach((parent) => {
          if (parent === window) {
            window.addEventListener('scroll', repositionPicker, {
              passive: true,
            });
          } else {
            parent.addEventListener('scroll', repositionPicker, {
              passive: true,
            });
          }
        });
        window.addEventListener('resize', repositionPicker);

        removePositionListeners = () => {
          parents.forEach((parent) => {
            if (parent === window) {
              window.removeEventListener('scroll', repositionPicker);
            } else {
              parent.removeEventListener('scroll', repositionPicker);
            }
          });
          window.removeEventListener('resize', repositionPicker);
          removePositionListeners = null;
        };
      };

      const removeListenersIfNeeded = () => {
        if (removeGlobalListeners) {
          removeGlobalListeners();
        }
        if (removePositionListeners) {
          removePositionListeners();
        }
      };

      const picker = new AirDatepicker(anchor, {
        range: true,
        autoClose: false,
        locale: localeEn,
        container: datepickerContainer,
        position({ $datepicker }) {
          positionPicker($datepicker, startDisplay, datepickerContainer);
        },
        buttons: ['clear'],
        dateFormat: 'yyyy-MM-dd',
        selectedDates: selectedDates.length ? selectedDates : undefined,
        onShow() {
          isPickerOpen = true;
          lastSelectedCount = picker.selectedDates.length;
          addGlobalListeners();
          addPositionListeners();
          repositionPicker();
        },
        onHide() {
          isPickerOpen = false;
          removeListenersIfNeeded();
        },
        onSelect({ date }) {
          const nextSelectedCount = picker.selectedDates.length;
          const shouldAutoClose =
            nextSelectedCount === 2 && lastSelectedCount === 1;
          lastSelectedCount = nextSelectedCount;

          if (!date) {
            startValue.value = '';
            endValue.value = '';
            startDisplay.value = '';
            endDisplay.value = '';
            dispatchValueChange(startValue);
            dispatchValueChange(endValue);
            picker.hide();
            return;
          }

          if (Array.isArray(date)) {
            const [first, second] = date;
            startValue.value = first ? formatLocalDate(first) : '';
            endValue.value = second ? formatLocalDate(second) : '';
            startDisplay.value = first ? formatDisplayDate(first) : '';
            endDisplay.value = second ? formatDisplayDate(second) : '';
            dispatchValueChange(startValue);
            dispatchValueChange(endValue);
            if (first && second && shouldAutoClose) {
              picker.hide();
            }
            return;
          }

          startValue.value = formatLocalDate(date);
          endValue.value = '';
          startDisplay.value = formatDisplayDate(date);
          endDisplay.value = '';
          dispatchValueChange(startValue);
          dispatchValueChange(endValue);
        },
      });

      const isDisabled = () =>
        startDisplay.disabled ||
        endDisplay.disabled ||
        startValue.disabled ||
        endValue.disabled;

      const showPicker = () => {
        if (isDisabled()) {
          return;
        }

        syncPickerFromInputs(picker, startValue, endValue);
        picker.show();
      };

      startDisplay.addEventListener('focus', showPicker);
      startDisplay.addEventListener('click', showPicker);
      endDisplay.addEventListener('focus', showPicker);
      endDisplay.addEventListener('click', showPicker);

      const syncFromValues = () => {
        if (isDisabled()) {
          return;
        }

        syncDisplayFromValues(startDisplay, endDisplay, startValue, endValue);
        syncPickerFromInputs(picker, startValue, endValue);
        lastSelectedCount = picker.selectedDates.length;
      };

      startValue.addEventListener('change', syncFromValues);
      endValue.addEventListener('change', syncFromValues);
    },
  );
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRangeDatepickers);
} else {
  initializeRangeDatepickers();
}
